import type { Question, Answer } from '@/types';
import { parseCurrency } from '@/utils/validators';

export function processAnswer(
  question: Question,
  rawValue: string | string[] | boolean
): Answer {
  let score = 0;
  let processedValue: string | string[] | number | boolean = rawValue;

  switch (question.type) {
    case 'boolean':
      processedValue = rawValue === true || rawValue === 'true' || rawValue === 'Sim';
      score = processedValue ? question.maxScore : 0;
      break;

    case 'single':
      if (question.options) {
        const option = question.options.find(
          o => o.value === rawValue || o.label === rawValue
        );
        if (option) {
          processedValue = option.value;
          score = option.score;
        }
      }
      break;

    case 'multiple':
      if (Array.isArray(rawValue) && question.options) {
        const selectedValues = rawValue as string[];
        processedValue = selectedValues;
        score = selectedValues.reduce((total, val) => {
          const option = question.options?.find(
            o => o.value === val || o.label === val
          );
          return total + (option?.score || 0);
        }, 0);
        score = Math.min(score, question.maxScore);
      }
      break;

    case 'currency': {
      const numericValue = parseCurrency(rawValue as string);
      processedValue = numericValue;
      score = 0;
      if (question.scoringThresholds && question.scoringThresholds.length > 0) {
        for (const threshold of question.scoringThresholds) {
          if (threshold.maxValue === undefined || numericValue <= threshold.maxValue) {
            score = threshold.score;
            break;
          }
        }
      }
      break;
    }

    case 'number':
    case 'percentage':
      processedValue = parseFloat(rawValue as string) || 0;
      break;

    case 'text':
      processedValue = rawValue as string;
      score = 0;
      break;
  }

  return {
    questionId: question.id,
    value: processedValue,
    score,
    timestamp: new Date()
  };
}


export function getOptionLabel(question: Question, value: string): string {
  if (question.options) {
    const option = question.options.find(o => o.value === value);
    return option?.label || value;
  }
  return value;
}

export function formatAnswerForDisplay(
  question: Question,
  answer: Answer
): string {
  const value = answer.value;

  switch (question.type) {
    case 'boolean':
      return value ? 'Sim' : 'Não';

    case 'single':
      return getOptionLabel(question, value as string);

    case 'multiple':
      if (Array.isArray(value)) {
        return value
          .map(v => getOptionLabel(question, v))
          .join(', ');
      }
      return String(value);

    case 'currency':
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value as number);

    case 'percentage':
      return `${value}%`;

    default:
      return String(value);
  }
}
